import Component from '@glimmer/component';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { on } from '@ember/modifier';
import { fn, hash } from '@ember/helper';
import { task } from 'ember-concurrency';
import { eq, or } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import cannot from 'ember-can/helpers/cannot';
import { tracked } from 'tracked-built-ins';
import {
  HdsPageHeader,
  HdsFormTextInputField,
  HdsFormHelperText,
  HdsFormSelectField,
  HdsFormLabel,
  HdsSeparator,
  HdsButtonSet,
  HdsButton,
  HdsLinkStandalone,
  HdsTabs,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  LimitBanner,
  FlexGrid,
  Typography,
  StickyFooter,
  DisclosureGuide,
  CodeBlock,
  CopyCard,
  WithErrors,
} from 'core/components';
import { boxMargin, boxPadding } from 'core/modifiers';
import { TRANSIT_GATEWAY_ATTACHMENT_SUBMITTED } from 'core/utils/consts/analytics-events/platform';
import templateString from 'core/utils/template-string';
import awsRegionList from 'core/utils/aws-region-list';
import { TYPE_NETWORK_TRANSIT_GATEWAY_ATTACHMENT } from 'common/utils/cloud-resource-types';
import { CONTENT_TABS_LABELS } from 'networks-common/utils/content-tabs';
import {
  AWS_CONNECTIONS_INFO,
  AWS_DISCLOSURE_GUIDES_TGW_ATTACHMENTS,
} from 'networks-common/utils/aws-connections';
import UpNext from 'networks-common/components/networks/cards/up-next';

let resourceShareTemplate = templateString`aws ram --region ${'region'} create-resource-share --name hcp-${'hvnId'}-resource-share --resource-arns ${'arn'} --principals ${'principal'}`;
let transitGatewayARNTemplate = templateString`arn:aws:ec2:${'region'}:${'accountId'}:transit-gateway/${'tgwId'}`;

const baseKey = 'components.page.hvn-transit-gateway-attachments.create.form';

/**
 *
 * `model` will be a model containing the network and transit gateway
 * attachments;
 * @argument model;
 * @type {Object}
 *
 */
/**
 *
 * `PageHvnTransitGatewayAttachmentsCreate` renders the HVN Transit Gateway
 * Attachment Create page.
 *
 *
 * ```
 * <Page::HvnTransitGatewayAttachments::Create
 *   @model={{@model}}
 * />
 * ```
 *
 * @class PageHvnTransitGatewayAttachmentsCreate
 *
 */
export default class PageHvnTransitGatewayAttachmentsCreate extends Component {
  @service analytics;
  @service api;
  @service abilities;
  @service flashMessages;
  @service intl;
  @service router;

  @tracked errors;
  @tracked cidr = '';
  @tracked cidrs = [];
  @tracked region = this.args.model.network.location.region.region;
  @tracked principalId =
    this.args.model.network.providerNetworkData.awsNetworkData.accountId;
  @tracked steps = [
    'components.page.hvn-transit-gateway-attachments.create.steps.create',
    'components.page.hvn-transit-gateway-attachments.create.steps.accept',
    'components.page.hvn-transit-gateway-attachments.create.steps.add-routes',
    'components.page.hvn-transit-gateway-attachments.create.steps.configure',
  ];

  awsRegions = awsRegionList;
  disclosureGuides = AWS_DISCLOSURE_GUIDES_TGW_ATTACHMENTS;
  tabs = CONTENT_TABS_LABELS;

  get resourceType() {
    return TYPE_NETWORK_TRANSIT_GATEWAY_ATTACHMENT;
  }

  get codeSnippetButtonDisabled() {
    return !this.renderValidCodeSnippet;
  }

  get renderValidCodeSnippet() {
    let requiredSnippetFields = ['accountId', 'transitGatewayId'];
    let emptyFields = [];

    let accountIdError = this.form.validationErrors.accountId;
    let tgwIdError = this.form.validationErrors.transitGatewayId;

    // Check if requiredSnippetFields are blank
    requiredSnippetFields.map((fieldName) => {
      for (const [key, value] of Object.entries(this.form.fields)) {
        if (key === fieldName && value === '') {
          emptyFields.push(fieldName);
        }
      }
    });

    // If requiredSnippetFields are blank or if they each have validation errors return false
    if (accountIdError.length || tgwIdError.length || emptyFields.length) {
      return false;
    } else {
      return true;
    }
  }
  get resourceShareCommand() {
    //only generate arn in cli command if AWS Account ID and Transit gateway ID fields are valid
    if (this.renderValidCodeSnippet) {
      let arn = transitGatewayARNTemplate({
        accountId: this.form.fields.accountId,
        tgwId: this.form.fields.transitGatewayId,
        region: this.region,
      });
      let string = resourceShareTemplate({
        principal: this.principalId,
        hvnId: this.args.model.network.id,
        arn: arn,
        region: this.region,
      });
      return string;
    } else {
      return this.intl.t(
        'components.page.hvn-transit-gateway-attachments.create.code-snippet.message'
      );
    }
  }

  checkElementValidity(element) {
    return element !== '';
  }

  updateQueryParams = (queryParams = {}) => {
    return this.router.transitionTo(this.router.currentRouteName, {
      queryParams,
    });
  };

  //TO DO - when HVN Routes API is ready, remove addCidr and removeCidr methods below
  addCidr = (newCidr) => {
    let exists = this.cidrs.find((cidr) => {
      return cidr === newCidr;
    });

    if (exists) {
      return;
    }

    if (!this.checkElementValidity(newCidr)) {
      return;
    }

    this.cidr = '';
    this.cidrs = [...this.cidrs, newCidr];
  };

  save = task(async (evt) => {
    // don't submit the form

    evt.preventDefault();
    this.validateForm();

    evt.preventDefault();
    this.addCidr(this.cidr);

    let {
      location: { organizationId, projectId },
      id: hvnId,
    } = this.args.model.network;
    let { network } = this.args.model;

    let id = this.form.fields.attachmentId;
    let transitGatewayId = this.form.fields.transitGatewayId;
    let resourceShareArn = this.form.fields.resourceShareArn;
    let cidrs = this.cidrs;
    let payload = {
      id,
      providerData: {
        awsData: {
          tgwId: transitGatewayId,
          resourceShareArn: resourceShareArn,
        },
      },
      cidrs: cidrs,
      hvn: network,
    };

    this.analytics.trackEvent(TRANSIT_GATEWAY_ATTACHMENT_SUBMITTED, payload);

    await this.api.network.createTGWAttachment(
      organizationId,
      projectId,
      hvnId,
      payload
    );

    this.flashMessages.success(
      this.intl.t(
        'components.page.hvn-transit-gateway-attachments.create.success.title'
      ),
      {
        content: this.intl.t(
          'components.page.hvn-transit-gateway-attachments.create.success.message'
        ),
      }
    );
    let routeClass = getOwner(this).lookup(
      `route:${AWS_CONNECTIONS_INFO.tgwAttachment.list}`
    );
    routeClass?.refresh();
    return this.router.replaceWith(AWS_CONNECTIONS_INFO.tgwAttachment.list);
  });

  /** form declaration */

  form = {
    fields: tracked({
      attachmentId: '',
      accountId: '',
      transitGatewayId: '',
      resourceShareArn: '',
    }),
    validationErrors: tracked({
      attachmentId: [],
      accountId: [],
      transitGatewayId: [],
      resourceShareArn: [],
    }),
    fieldValidators: {
      attachmentId: [
        {
          message: this.intl.t(`${baseKey}.errors.attachment-id.required`),
          isValid: (v) => v !== '',
        },
      ],
      accountId: [
        {
          message: this.intl.t(`${baseKey}.errors.account-id.required`),
          isValid: (v) => v !== '',
          subValidators: [
            {
              message: this.intl.t(
                `${baseKey}.errors.account-id.must-match-account-id-pattern`
              ),
              isValid: (v) => this.accountIdMatchesPattern.test(v),
            },
          ],
        },
      ],
      transitGatewayId: [
        {
          message: this.intl.t(`${baseKey}.errors.transit-gateway-id.required`),
          isValid: (v) => v && v !== '',
          subValidators: [
            {
              message: this.intl.t(
                `${baseKey}.errors.transit-gateway-id.must-match-tgw-pattern`
              ),
              isValid: (v) => this.nameMatchesTgwPattern.test(v),
            },
          ],
        },
      ],
      resourceShareArn: [
        {
          message: this.intl.t(`${baseKey}.errors.resource-share-arn.required`),
          isValid: (v) => v !== '',
        },
      ],
    },
  };

  nameMatchesTgwPattern = /^(tgw-)([A-Za-z0-9]+)$/;
  accountIdMatchesPattern = /^\d{12}$/;

  setAttachmentId = (e) => {
    this.form.fields.attachmentId = e.target.value;
    this.validateForm('attachmentId');
  };

  setAccountId = (e) => {
    this.form.fields.accountId = e.target.value;
    this.validateForm('accountId');
  };

  setTransitGatewayId = (e) => {
    this.form.fields.transitGatewayId = e.target.value;
    this.validateForm('transitGatewayId');
  };

  setResourceShareArn = (e) => {
    this.form.fields.resourceShareArn = e.target.value;
    this.validateForm('resourceShareArn');
  };

  validateForm(...changedFormFields) {
    for (var i = 0; i < changedFormFields.length; i++) {
      let fieldName = changedFormFields[i];
      let fieldErrors = this.runValidators(
        this.form.fieldValidators[fieldName],
        this.form.fields[fieldName]
      );
      this.form.validationErrors[fieldName] =
        fieldErrors.map((v) => v.message) || [];
    }
    return !Object.keys(this.form.validationErrors).find(
      (k) => this.form.validationErrors[k].length
    );
  }

  runValidators(validators, value) {
    let failedValidators = validators.flatMap((v) => {
      if (!v.isValid(value)) {
        return v;
      }

      if (v.subValidators) {
        return this.runValidators(v.subValidators, value);
      }

      return [];
    });

    return failedValidators;
  }
  <template>
    <WithErrors @error={{this.save.last.error}} as |FormError FieldError|>
      <CoreLayoutMain>
        <:header>
          <HdsPageHeader as |PH|>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
            <PH.Title>
              {{t
                "components.page.hvn-transit-gateway-attachments.create.title"
              }}
            </PH.Title>
          </HdsPageHeader>
        </:header>
        <:alert>
          <LimitBanner
            @padding="0"
            @variant={{this.resourceType}}
            @count={{@model.tgwAttachments.length}}
            as |A|
          >
            <A.LinkStandalone
              @icon="arrow-right"
              @iconPosition="trailing"
              @route="cloud.orgs.detail.projects.detail.hvns.detail.transit-gateway-attachments.list"
              @text={{t
                "components.limit-banner.create-cta.network-transit-gateway-attachments"
              }}
            />
          </LimitBanner>
          <FormError />
        </:alert>
        <:body>
          <FlexGrid as |G|>
            <G.Item @xs="12" @sm="12" @md="8" @lg="5">
              <form
                class="hcp-hds-form"
                data-test-form
                {{on "submit" this.save.perform}}
              >
                <Typography
                  @variant="body1"
                  class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                  {{boxMargin "0 0 md 0"}}
                >
                  {{t
                    "components.page.hvn-transit-gateway-attachments.create.description"
                    htmlSafe=true
                  }}
                </Typography>
                <ol {{boxMargin "0 0 lg 0"}}>
                  {{#each this.steps as |step index|}}
                    <li
                      class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                      {{boxPadding (if (eq index 0) "0 0 0 0" "xs 0 0 0")}}
                    >
                      {{t step}}
                    </li>
                  {{/each}}
                </ol>
                <HdsLinkStandalone
                  @href="https://learn.hashicorp.com/tutorials/cloud/amazon-transit-gateway?in=cloud/networking"
                  @icon="docs-link"
                  @iconPosition="leading"
                  @isHrefExternal={{true}}
                  @text={{t
                    "components.page.hvn-transit-gateway-attachments.create.get-started.learn.title"
                  }}
                />

                <HdsTabs {{boxPadding "xl 0 0 0"}} as |T|>
                  <T.Tab
                    @isSelected={{eq @tab this.tabs.terminal.id}}
                    data-test-transit-gateway-attachment-terminal-tab
                    {{on
                      "click"
                      (fn
                        this.updateQueryParams (hash tab=this.tabs.terminal.id)
                      )
                    }}
                  >
                    {{t this.tabs.terminal.label}}
                  </T.Tab>
                  <T.Panel />

                  <T.Tab
                    @isSelected={{eq @tab this.tabs.webConsole.id}}
                    data-test-transit-gateway-attachment-web-console-tab
                    {{on
                      "click"
                      (fn
                        this.updateQueryParams
                        (hash tab=this.tabs.webConsole.id)
                      )
                    }}
                  >
                    {{t this.tabs.webConsole.label}}
                  </T.Tab>
                  <T.Panel />
                </HdsTabs>
                <section {{boxPadding "lg 0 xl 0"}}>
                  <div {{boxPadding "xs 0"}}>
                    <FieldError
                      @field="id"
                      @hdsDelegatedError={{true}}
                      as |err|
                    >
                      <HdsFormTextInputField
                        @id="attachmentId"
                        @isInvalid={{or
                          this.form.validationErrors.attachmentId.length
                          err
                        }}
                        @type="text"
                        @value={{this.form.fields.attachmentId}}
                        name="attachmentId"
                        data-test-attachment-id
                        data-test-input
                        {{on "input" this.setAttachmentId}}
                        {{on "blur" this.setAttachmentId}}
                        {{on "change" this.setAttachmentId}}
                        as |F|
                      >
                        <F.Label @controlId="id">
                          {{t
                            "components.page.hvn-transit-gateway-attachments.create.form.label.attachment-id"
                          }}
                        </F.Label>
                        <F.HelperText @controlId="idHelp">
                          {{t
                            "components.page.hvn-transit-gateway-attachments.create.form.label.attachment-id-help"
                          }}
                        </F.HelperText>
                        {{#if err}}
                          <F.Error>
                            {{err.description}}
                          </F.Error>
                        {{/if}}
                        {{#unless err}}
                          {{#each
                            this.form.validationErrors.attachmentId
                            as |m|
                          }}
                            <F.Error
                              id="attachmentId-error"
                              data-test-inline-validation-error="attachmentId"
                              data-test-validation-attachment-id-error
                            >
                              {{m}}
                            </F.Error>
                          {{/each}}
                        {{/unless}}
                      </HdsFormTextInputField>
                    </FieldError>
                  </div>
                  <br />
                  <HdsFormHelperText @controlId="createAttachmentStep1">
                    {{t
                      "components.page.hvn-transit-gateway-attachments.create.form.header.step-1"
                    }}
                  </HdsFormHelperText>
                  <Typography
                    @variant="h3"
                    class="hds-typography-display-300 hds-font-weight-bold"
                    {{boxPadding "2xs 0 xs 0"}}
                  >
                    {{t
                      "components.page.hvn-transit-gateway-attachments.create.form.header.resource-share"
                    }}
                  </Typography>
                  <Typography
                    @variant="body1"
                    class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                    {{boxPadding "0 0 lg 0"}}
                  >
                    {{#if (eq @tab this.tabs.terminal.id)}}
                      {{t
                        "components.page.hvn-transit-gateway-attachments.create.form.instructions.resource-share.terminal"
                      }}
                    {{else if (eq @tab this.tabs.webConsole.id)}}
                      {{t
                        "components.page.hvn-transit-gateway-attachments.create.form.instructions.resource-share.web-console"
                        ramLink="https://console.aws.amazon.com/ram/home"
                        htmlSafe=true
                      }}
                    {{/if}}
                  </Typography>
                  {{#if (eq @tab this.tabs.terminal.id)}}
                    <FieldError
                      @field="accountId"
                      @hdsDelegatedError={{true}}
                      as |err|
                    >
                      <HdsFormTextInputField
                        @id="accountId"
                        @isInvalid={{or
                          this.form.validationErrors.accountId.length
                          err
                        }}
                        @type="text"
                        @value={{this.form.fields.accountId}}
                        name="accountId"
                        placeholder={{t
                          "components.page.hvn-transit-gateway-attachments.create.form.label.aws-account-id-placeholder"
                        }}
                        data-test-account-id
                        data-test-input
                        {{on "input" this.setAccountId}}
                        {{on "blur" this.setAccountId}}
                        {{on "change" this.setAccountId}}
                        as |F|
                      >
                        <F.Label @controlId="accountId">
                          {{t
                            "components.page.hvn-transit-gateway-attachments.create.form.label.aws-account-id"
                          }}
                        </F.Label>
                        <F.HelperText @controlId="accountIdHelp">
                          {{t
                            "components.page.hvn-transit-gateway-attachments.create.form.label.aws-account-id-help"
                          }}
                        </F.HelperText>
                        {{#if err}}
                          <F.Error>
                            {{err.description}}
                          </F.Error>
                        {{/if}}
                        {{#unless err}}
                          {{#each this.form.validationErrors.accountId as |m|}}
                            <F.Error
                              id="accountId-error"
                              data-test-inline-validation-error="accountId"
                              data-test-validation-attachment-id-error
                            >
                              {{m}}
                            </F.Error>
                          {{/each}}
                        {{/unless}}
                      </HdsFormTextInputField>
                    </FieldError>
                    <DisclosureGuide
                      @content={{this.disclosureGuides.accountId}}
                    />
                    <br />
                    <FieldError
                      @field="provider_data.Data.AwsData.tgw_id"
                      @hdsDelegatedError={{true}}
                      as |err|
                    >
                      <HdsFormTextInputField
                        @id="transitGatewayId--terminal"
                        @isInvalid={{or
                          this.form.validationErrors.transitGatewayId.length
                          err
                        }}
                        @type="text"
                        @value={{this.form.fields.transitGatewayId}}
                        name="transitGatewayId--terminal"
                        placeholder={{t
                          "components.page.hvn-transit-gateway-attachments.create.form.label.aws-transit-gateway-id-placeholder"
                        }}
                        data-test-input
                        data-test-transit-gateway-id
                        {{on "input" this.setTransitGatewayId}}
                        {{on "blur" this.setTransitGatewayId}}
                        {{on "change" this.setTransitGatewayId}}
                        as |F|
                      >
                        <F.Label @controlId="transitGatewayId--terminal">
                          {{t
                            "components.page.hvn-transit-gateway-attachments.create.form.label.aws-transit-gateway-id"
                          }}
                        </F.Label>
                        <F.HelperText
                          @controlId="transitGatewayId_Help--terminal"
                        >
                          {{t
                            "components.page.hvn-transit-gateway-attachments.create.form.label.aws-transit-gateway-id-help"
                          }}
                        </F.HelperText>
                        {{#if err}}
                          <F.Error>
                            {{err.description}}
                          </F.Error>
                        {{/if}}
                        {{#unless err}}
                          {{#each
                            this.form.validationErrors.transitGatewayId
                            as |m|
                          }}
                            <F.Error
                              id="transit-gateway-id--terminal-error"
                              data-test-inline-validation-error="transitGatewayId--terminal"
                              data-test-validation-transit-gateway-id-error
                            >
                              {{m}}
                            </F.Error>
                          {{/each}}
                        {{/unless}}
                      </HdsFormTextInputField>
                    </FieldError>
                    <DisclosureGuide @content={{this.disclosureGuides.tgwId}} />
                    <br />
                    <HdsFormSelectField
                      @extraAriaDescribedBy="vpcRegionHelp"
                      @id="vpcRegion"
                      name="vpcRegion"
                      selected={{this.region}}
                      disabled="disabled"
                      value={{this.region}}
                      data-test-input
                      as |F|
                    >
                      <F.Label @controlId="vpcRegion">
                        {{t
                          "components.page.hvn-transit-gateway-attachments.create.form.label.vpc-region"
                        }}
                      </F.Label>
                      <F.HelperText @controlId="accountRegionHelp">
                        {{t
                          "components.page.hvn-transit-gateway-attachments.create.form.label.vpc-region-help"
                        }}
                      </F.HelperText>
                      <F.Options>
                        {{#each this.awsRegions as |region|}}
                          <option>
                            {{region}}
                          </option>
                        {{/each}}
                      </F.Options>
                    </HdsFormSelectField>
                    <br />
                    <div {{boxPadding "xs 0 0 0"}}>
                      <HdsFormLabel @controlId="resourceShareCommand">
                        {{t
                          "components.page.hvn-transit-gateway-attachments.create.form.label.aws-resource-share-command"
                        }}
                      </HdsFormLabel>
                      <CodeBlock
                        @disabled={{this.codeSnippetButtonDisabled}}
                        data-test-tgw-create-resource-share-codeblock
                      >
                        {{this.resourceShareCommand}}
                      </CodeBlock>
                    </div>
                  {{else if (eq @tab this.tabs.webConsole.id)}}
                    <div
                      class="transit-gateway-principal-id"
                      data-test-copy-card-principal-id
                      {{boxPadding "xs 0 0 0"}}
                    >
                      <HdsFormLabel
                        @controlId="accountId"
                        {{boxPadding "0 0 2xs 0"}}
                      >
                        {{t
                          "components.page.hvn-transit-gateway-attachments.create.form.label.aws-account-id"
                        }}
                      </HdsFormLabel>
                      <HdsFormHelperText
                        @controlId="accountId"
                        {{boxPadding "0 0 sm 0"}}
                      >
                        {{t
                          "components.page.hvn-transit-gateway-attachments.create.form.label.aws-account-id-copy-help"
                        }}
                      </HdsFormHelperText>
                      <CopyCard
                        @clipboardText={{this.principalId}}
                        @container=".transit-gateway-principal-id"
                      />
                    </div>
                  {{/if}}
                </section>
                <section {{boxPadding "0 0 xs 0"}}>
                  <HdsFormHelperText @controlId="createAttachmentStep2">
                    {{t
                      "components.page.hvn-transit-gateway-attachments.create.form.header.step-2"
                    }}
                  </HdsFormHelperText>
                  <Typography
                    @variant="h3"
                    class="hds-typography-display-300 hds-font-weight-bold"
                    {{boxPadding "2xs 0 xs 0"}}
                  >
                    {{t
                      "components.page.hvn-transit-gateway-attachments.create.form.header.transit-gateway-details"
                    }}
                  </Typography>
                  <Typography
                    @variant="body1"
                    class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                    {{boxPadding "0 0 md 0"}}
                  >
                    {{#if (eq @tab this.tabs.terminal.id)}}
                      {{t
                        "components.page.hvn-transit-gateway-attachments.create.form.instructions.transit-gateway-details.terminal"
                      }}
                    {{else if (eq @tab this.tabs.webConsole.id)}}
                      {{t
                        "components.page.hvn-transit-gateway-attachments.create.form.instructions.transit-gateway-details.web-console"
                      }}
                    {{/if}}
                  </Typography>
                  {{#if (eq @tab this.tabs.webConsole.id)}}
                    <FieldError
                      @field="provider_data.Data.AwsData.tgw_id"
                      @hdsDelegatedError={{true}}
                      as |err|
                    >
                      <HdsFormTextInputField
                        @id="transitGatewayId--webConsole"
                        @isInvalid={{or
                          this.form.validationErrors.transitGatewayId.length
                          err
                        }}
                        @type="text"
                        @value={{this.form.fields.transitGatewayId}}
                        name="transitGatewayId--webConsole"
                        placeholder={{t
                          "components.page.hvn-transit-gateway-attachments.create.form.label.aws-transit-gateway-id-placeholder"
                        }}
                        data-test-input
                        data-test-transit-gateway-id
                        {{on "input" this.setTransitGatewayId}}
                        {{on "blur" this.setTransitGatewayId}}
                        {{on "change" this.setTransitGatewayId}}
                        {{! aria-describedby='transitGatewayId_Help--webConsole' }}
                        as |F|
                      >
                        <F.Label @controlId="transitGatewayId--webConsole">
                          {{t
                            "components.page.hvn-transit-gateway-attachments.create.form.label.aws-transit-gateway-id"
                          }}
                        </F.Label>
                        <F.HelperText @controlId="transitGatewayId--webConsole">
                          {{t
                            "components.page.hvn-transit-gateway-attachments.create.form.label.aws-transit-gateway-id-help"
                          }}
                        </F.HelperText>
                      </HdsFormTextInputField>
                    </FieldError>
                    <DisclosureGuide @content={{this.disclosureGuides.tgwId}} />
                    <br />
                  {{/if}}
                  <FieldError
                    @field="provider_data.Data.AwsData.resource_share_arn"
                    @hdsDelegatedError={{true}}
                    as |err|
                  >
                    <HdsFormTextInputField
                      @id="resourceShareArn"
                      @isInvalid={{or
                        this.form.validationErrors.resourceShareArn.length
                        err
                      }}
                      @type="text"
                      @value={{this.form.fields.resourceShareArn}}
                      name="resourceShareArn"
                      placeholder={{t
                        "components.page.hvn-transit-gateway-attachments.create.form.label.aws-resource-share-arn-placeholder"
                      }}
                      data-test-resource-share-arn
                      data-test-input
                      {{on "input" this.setResourceShareArn}}
                      {{on "blur" this.setResourceShareArn}}
                      {{on "change" this.setResourceShareArn}}
                      as |F|
                    >
                      <F.Label @controlId="resourceShareArn">
                        {{t
                          "components.page.hvn-transit-gateway-attachments.create.form.label.aws-resource-share-arn"
                        }}
                      </F.Label>
                      <F.HelperText @controlId="resourceShareArnHelp">
                        {{t
                          "components.page.hvn-transit-gateway-attachments.create.form.label.aws-resource-share-arn-help"
                        }}
                      </F.HelperText>
                      {{#if err}}
                        <F.Error>
                          {{err.description}}
                        </F.Error>
                      {{/if}}
                      {{#unless err}}
                        {{#each
                          this.form.validationErrors.resourceShareArn
                          as |m|
                        }}
                          <F.Error
                            id="resource-share-arn-error"
                            data-test-inline-validation-error="resourceShareArn"
                            data-test-validation-resource-share-arn-error
                          >
                            {{m}}
                          </F.Error>
                        {{/each}}
                      {{/unless}}
                    </HdsFormTextInputField>
                  </FieldError>
                  <DisclosureGuide
                    @content={{this.disclosureGuides.resourceShareArn}}
                  />
                </section>
                <HdsSeparator />
                <div {{boxPadding "sm 0 0 0"}}>
                  <UpNext @resourceType={{this.resourceType}} />
                </div>
                <StickyFooter>
                  <HdsButtonSet>
                    <HdsButton
                      @color="primary"
                      @text={{t
                        "components.page.hvn-transit-gateway-attachments.create.action"
                      }}
                      disabled={{or
                        (cannot
                          "create network-tgw-attachment" @model.tgwAttachments
                        )
                        this.save.isRunning
                      }}
                      type="submit"
                      data-test-submit
                    />
                    <HdsButton
                      @color="secondary"
                      @route="cloud.orgs.detail.projects.detail.hvns.detail.transit-gateway-attachments.list"
                      @text={{t "components.form.cancel"}}
                      data-test-cancel
                    />
                  </HdsButtonSet>
                </StickyFooter>
              </form>
            </G.Item>
          </FlexGrid>
        </:body>
      </CoreLayoutMain>
    </WithErrors>
  </template>
}
